import type { Building } from "building-types/building/buildingTypes";
import { type BuiltInLabel } from "building-types/entities/labelTypes";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import {
  BrochureFloorPlanSummaryTagOrder,
  ElectricalPlanTagOrder,
  FloorPlanMaterialsTagOrder,
  FloorPlanSummaryTagOrder,
  FloorPlanTagOrder,
  FoundationPlanTagOrder,
  StructuralPlanTagOrder,
  EmptyFloorPlanSummaryTagOrder,
  RoofPlanTagOrder,
  FoundationUtilityPlanTagOrderWithUtilitySlab,
  BackgroundPlanTagOrder,
} from "doc/planGeometryBuiltInTagOrders";
import type { SheetGeometrySourceFilter } from "doc/plans/planGeometrySource";
import type { SheetGeometryStyler } from "doc/plans/planGeometrySourceTypes";
import type { SheetGeometrySelector } from "doc/plans/planGeometrySourceTypes";
import { makeSheetGeometrySourceFilter, makeSheetGeometryStyler } from "doc/plans/sheetGeometryEffects";
import {
  OverridableSheetGeometryCategoriesNoProps,
  OverridableSheetGeometryCategoriesNoTags,
} from "doc/plans/sheetGeometryOverridableCategories";
import type { SheetGeometryCategoryStyleSheets } from "doc/plans/SheetGeometrySourceStyleOverrideTable";
import { entityContainsBuiltInLabels } from "doc/utils/builtInLabelUtils";
import type { BuiltInFilterSelectorsAndFilters } from "./planGeometryBuiltInPlanFilters";
import {
  makeBuiltInBrochureFloorPlanSummarySelectorsAndFilters,
  makeBuiltInElectricalPlanSelectorsAndFilters,
  makeBuiltInFloorPlanSelectorsAndFilters,
  makeBuiltInFloorPlanMaterialsSelectorsAndFilters,
  makeBuiltInFloorPlanSummarySelectorsAndFilters,
  makeBuiltInStructuralPlanSelectorsAndFilters,
  makeBuiltInFoundationUtilityPlanFilter,
  makeBuiltInBackgroundPlanSelectorsAndFilters,
  makeBuiltInFoundationPlanSelectorsAndFiltersNoSetting,
  makeBuiltInFoundationPlanSelectorsAndFilters,
} from "./planGeometryBuiltInPlanFilters";
import {
  makeBrochureFloorPlanSummaryLayerStyleOverrides,
  makeElectricalPlanLayerStyleOverrides,
  makeFloorplanLayerStyleOverrides,
  makeFloorPlanMaterialsLayerStyleOverrides,
  makeFloorPlanSummaryLayerStyleOverrides,
  makeFoundationUtilityPlanStyleOverrides,
  makeStructuralLayerStyleOverrides,
} from "./planGeometryBuiltInPlanStyles";

export const BuiltInPlanTypeLabel: { [key in BuiltinPlanViewType]: BuiltInLabel | undefined } = {
  [BuiltinPlanViewType.FLOOR]: "addToFloorPlan",
  [BuiltinPlanViewType.FOUNDATION]: "addToFoundationPlan",
  [BuiltinPlanViewType.ELECTRICAL]: "addToElectricalPlan",
  [BuiltinPlanViewType.BROCHURE]: "addToBrochurePlan",
  [BuiltinPlanViewType.FLOOR_MATERIALS]: "addToFloorMaterialsPlan",
  [BuiltinPlanViewType.FLOOR_SUMMARY]: "addToFloorSummaryPlan",
  [BuiltinPlanViewType.STRUCTURAL]: "addToStructuralPlan",
  [BuiltinPlanViewType.ROOF]: "addToRoofPlan",
  [BuiltinPlanViewType.UTILITY]: "addToUtilityPlan",
  [BuiltinPlanViewType.BACKGROUND]: undefined,
  [BuiltinPlanViewType.EMPTY]: undefined,
};

export const BuiltInPlanTypeCategories: { [key in BuiltinPlanViewType]: readonly SheetGeometryCategory[] } = {
  [BuiltinPlanViewType.FLOOR]: FloorPlanTagOrder,
  [BuiltinPlanViewType.FOUNDATION]: FoundationPlanTagOrder,
  [BuiltinPlanViewType.ELECTRICAL]: ElectricalPlanTagOrder,
  [BuiltinPlanViewType.BROCHURE]: BrochureFloorPlanSummaryTagOrder,
  [BuiltinPlanViewType.FLOOR_MATERIALS]: FloorPlanMaterialsTagOrder,
  [BuiltinPlanViewType.FLOOR_SUMMARY]: FloorPlanSummaryTagOrder,
  [BuiltinPlanViewType.STRUCTURAL]: StructuralPlanTagOrder,
  [BuiltinPlanViewType.ROOF]: RoofPlanTagOrder,
  [BuiltinPlanViewType.UTILITY]: FoundationUtilityPlanTagOrderWithUtilitySlab,
  [BuiltinPlanViewType.BACKGROUND]: BackgroundPlanTagOrder,
  [BuiltinPlanViewType.EMPTY]: [],
};

export const BuiltInPlanTypeStyleOverrides: {
  [key in BuiltinPlanViewType]: (floorIndex?: number) => SheetGeometryCategoryStyleSheets;
} = {
  [BuiltinPlanViewType.FLOOR]: makeFloorplanLayerStyleOverrides,
  [BuiltinPlanViewType.ELECTRICAL]: makeElectricalPlanLayerStyleOverrides,
  [BuiltinPlanViewType.BROCHURE]: makeBrochureFloorPlanSummaryLayerStyleOverrides,
  [BuiltinPlanViewType.FLOOR_MATERIALS]: makeFloorPlanMaterialsLayerStyleOverrides,
  [BuiltinPlanViewType.STRUCTURAL]: makeStructuralLayerStyleOverrides,
  [BuiltinPlanViewType.FLOOR_SUMMARY]: makeFloorPlanSummaryLayerStyleOverrides,
  [BuiltinPlanViewType.UTILITY]: makeFoundationUtilityPlanStyleOverrides,
  [BuiltinPlanViewType.BACKGROUND]: () => ({}),
  [BuiltinPlanViewType.FOUNDATION]: () => ({}),
  [BuiltinPlanViewType.ROOF]: () => ({}),
  [BuiltinPlanViewType.EMPTY]: () => ({}),
};

export function makeBuiltInPlanTypeSelectorsAndFilters(
  referenceType: BuiltinPlanViewType,
  noSettings: boolean = false,
): BuiltInFilter {
  switch (referenceType) {
    case BuiltinPlanViewType.FLOOR:
      return makeBuiltInFloorPlanSelectorsAndFilters;
    case BuiltinPlanViewType.FOUNDATION:
      if (noSettings) {
        return makeBuiltInFoundationPlanSelectorsAndFiltersNoSetting;
      } else {
        return makeBuiltInFoundationPlanSelectorsAndFilters;
      }
    case BuiltinPlanViewType.ELECTRICAL:
      return makeBuiltInElectricalPlanSelectorsAndFilters;
    case BuiltinPlanViewType.BROCHURE:
      return makeBuiltInBrochureFloorPlanSummarySelectorsAndFilters;
    case BuiltinPlanViewType.FLOOR_MATERIALS:
      return makeBuiltInFloorPlanMaterialsSelectorsAndFilters;
    case BuiltinPlanViewType.FLOOR_SUMMARY:
      return makeBuiltInFloorPlanSummarySelectorsAndFilters;
    case BuiltinPlanViewType.STRUCTURAL:
      return makeBuiltInStructuralPlanSelectorsAndFilters;
    case BuiltinPlanViewType.ROOF:
      return () => ({ builtInSelectors: {}, builtInFilters: {} });
    case BuiltinPlanViewType.UTILITY:
      return makeBuiltInFoundationUtilityPlanFilter;
    case BuiltinPlanViewType.BACKGROUND:
      return makeBuiltInBackgroundPlanSelectorsAndFilters;
    case BuiltinPlanViewType.EMPTY:
      return () => ({ builtInSelectors: {}, builtInFilters: {} });
  }
}

type BuiltInFilter = (building: Building, levelId: string, ...args: any[]) => BuiltInFilterSelectorsAndFilters;
type BuiltInFilterArgs = Parameters<BuiltInFilter>;

export function makeBuiltInPlanTypeCategories(referenceType: string): readonly SheetGeometryCategory[] {
  if (referenceType === BuiltinPlanViewType.EMPTY) {
    return EmptyFloorPlanSummaryTagOrder;
  }

  return BuiltInPlanTypeCategories[referenceType];
}

/** @deprecated */
export function makeBuiltInSelectorsAndFilters(
  referenceType: BuiltinPlanViewType | undefined,
  noSettings: boolean,
  ...args: BuiltInFilterArgs
): BuiltInFilterSelectorsAndFilters {
  if (!referenceType) {
    return { builtInSelectors: {}, builtInFilters: {} };
  }

  const makeFilterConditions = makeBuiltInPlanTypeSelectorsAndFilters(referenceType, noSettings);
  return makeFilterConditions(...args);
}

// Electrical controllers aren't part of the shared Overridable category lists, but should still
// respect addToXPlan labels, including on their native Electrical Plan (they default to
// addToElectricalPlan=true on creation and via migration, so removing the label hides them).
// Brochure is the one exception, kept unconditional so pre-labeling controllers still show.
const OverridableSheetGeometryCategoriesNoPropsWithControllers: SheetGeometryCategory[] = [
  ...OverridableSheetGeometryCategoriesNoProps,
  SheetGeometryCategory.ELECTRICAL_CONTROLLERS,
];
const OverridableSheetGeometryCategoriesNoTagsWithControllers: SheetGeometryCategory[] = [
  ...OverridableSheetGeometryCategoriesNoTags,
  SheetGeometryCategory.ELECTRICAL_CONTROLLERS,
];

function makeBuiltInLabelSelectorCategories(referenceType: BuiltinPlanViewType): SheetGeometryCategory[] {
  const label = BuiltInPlanTypeLabel[referenceType];
  if (label === undefined) {
    return [];
  }

  switch (referenceType) {
    case BuiltinPlanViewType.FOUNDATION:
    case BuiltinPlanViewType.UTILITY:
      return OverridableSheetGeometryCategoriesNoPropsWithControllers;
    case BuiltinPlanViewType.ELECTRICAL:
      return OverridableSheetGeometryCategoriesNoTagsWithControllers;
    case BuiltinPlanViewType.BROCHURE:
      return OverridableSheetGeometryCategoriesNoTags;
    default:
      return OverridableSheetGeometryCategoriesNoTagsWithControllers;
  }
}

/** @deprecated */
export function makeBuiltInSheetGeometrySourceFilterAndStyler(
  referenceType: BuiltinPlanViewType,
  ...filterArgs: BuiltInFilterArgs
): {
  filtering: SheetGeometrySourceFilter;
  styling: SheetGeometryStyler;
} {
  const label = BuiltInPlanTypeLabel[referenceType];

  const labelSelectors: SheetGeometryCategoryTable<SheetGeometrySelector> = {};
  if (label !== undefined) {
    const categories = [...makeBuiltInLabelSelectorCategories(referenceType), SheetGeometryCategory.ANNOT_DIM_GROUP];
    for (const category of categories) {
      labelSelectors[category] = (g) => entityContainsBuiltInLabels(g.buildingEntity, label);
    }
  }

  const { builtInSelectors, builtInFilters } = makeBuiltInSelectorsAndFilters(referenceType, false, ...filterArgs);

  const [building, levelId] = filterArgs;

  let builtInStyleOverrides: SheetGeometryCategoryStyleSheets = {};
  const builtInStyler = BuiltInPlanTypeStyleOverrides[referenceType];
  if (builtInStyler) {
    builtInStyleOverrides = builtInStyler(building.levels[levelId].floorIndex);
  }

  const mergedSelectors: SheetGeometryCategoryTable<SheetGeometrySelector> = {};

  // merge selectors
  for (const key of new Set<string>([...Object.keys(labelSelectors), ...Object.keys(builtInSelectors)])) {
    const category = key as SheetGeometryCategory;
    mergedSelectors[category] = (g) => {
      const labelSelector = labelSelectors[category];
      if (labelSelector && !labelSelector?.(g)) {
        return false;
      }

      const builtInSelector = builtInSelectors[category];
      if (builtInSelector && !builtInSelector?.(g)) {
        return false;
      }

      return true;
    };
  }

  return {
    filtering: makeSheetGeometrySourceFilter({
      builtInSelectors: mergedSelectors,
      builtInFilters,
    }),
    styling: makeSheetGeometryStyler({
      viewSpecificStyles: builtInStyleOverrides,
    }),
  };
}
